import { Position } from '@xyflow/react';
import type { Edge, Node } from '@xyflow/react';

import type {
  Mindmap,
  MindmapLayoutDirection,
  MindmapNode,
  MindmapNodeShape,
} from './MindmapTypes';

export interface MindmapNodeData extends Record<string, unknown> {
  id: MindmapNode['id'];
  text: MindmapNode['text'];
  parentId: MindmapNode['parentId'];
  order: MindmapNode['order'];
  collapsed?: MindmapNode['collapsed'];
  textColor?: MindmapNode['textColor'];
  bgColor?: MindmapNode['bgColor'];
  borderColor?: MindmapNode['borderColor'];
  shape?: MindmapNodeShape;
  isRoot?: boolean;
  layoutDirection?: MindmapLayoutDirection;
  hasChildren?: boolean;
  link?: MindmapNode['link'];
  fontSize?: MindmapNode['fontSize'];
  lineHeight?: MindmapNode['lineHeight'];
  boxShadow?: MindmapNode['boxShadow'];
  borderStyle?: MindmapNode['borderStyle'];
  borderWidth?: MindmapNode['borderWidth'];
  borderRadius?: MindmapNode['borderRadius'];
  styleTokens?: MindmapNode['styleTokens'];
  isGhost?: MindmapNode['isGhost'];
}

export interface MindmapReactFlowState {
  nodes: Node<MindmapNodeData>[];
  edges: Edge[];
}

export function validateMindmap(mindmap: Mindmap): Mindmap {
  if (!mindmap.nodes.length) {
    throw new Error('Mindmap must include the root node.');
  }

  const nodeIds = new Set(mindmap.nodes.map((node) => node.id));
  if (!nodeIds.has(mindmap.rootId)) {
    throw new Error('Mindmap rootId must reference an existing node.');
  }

  return mindmap;
}

export function buildReactFlowEdges(mindmap: Mindmap, mapId?: string): Edge[] {
  const ns = (id: string) => (mapId ? `${mapId}::${id}` : id);
  const validMindmap = validateMindmap(mindmap);
  return validMindmap.nodes
    .filter((n) => n.parentId)
    .map((n) => ({
      id: `e-${ns(n.parentId as string)}-${ns(n.id)}`,
      source: ns(n.parentId as string),
      target: ns(n.id),
      animated: false,
    }));
}

const directionToPositions = (
  direction: MindmapLayoutDirection
): { source: Position; target: Position } => {
  switch (direction) {
    case 'left':
      return { source: Position.Left, target: Position.Right };
    case 'up':
      return { source: Position.Top, target: Position.Bottom };
    case 'down':
      return { source: Position.Bottom, target: Position.Top };
    case 'right':
    default:
      return { source: Position.Right, target: Position.Left };
  }
};

export function buildReactFlowNodes(
  mindmap: Mindmap,
  positions: Record<string, { x: number; y: number }>,
  opts?: { mapId?: string; offset?: { x: number; y: number } }
): Node<MindmapNodeData>[] {
  const validMindmap = validateMindmap(mindmap);
  // Precompute children existence for each node
  const hasChildrenMap = (() => {
    const map = new Map<string, boolean>();
    for (const n of validMindmap.nodes) map.set(n.id, false);
    for (const n of validMindmap.nodes) {
      if (n.parentId != null) map.set(n.parentId, true);
    }
    return map;
  })();
  const ns = (id: string) => (opts?.mapId ? `${opts.mapId}::${id}` : id);
  const off = opts?.offset ?? { x: 0, y: 0 };
  const globalDirection = mindmap.layoutDirection ?? 'right';
  const { source, target } = directionToPositions(globalDirection);
  return validMindmap.nodes.map((node) => ({
    id: ns(node.id),
    type: 'mindmapNode',
    position: (() => {
      const self = positions[node.id];
      if (self) return { x: self.x + off.x, y: self.y + off.y };

      const parent = node.parentId ? positions[node.parentId] : undefined;
      if (parent) return { x: parent.x + off.x, y: parent.y + off.y };

      const root = positions[validMindmap.rootId];
      if (root) return { x: root.x + off.x, y: root.y + off.y };

      return { x: off.x, y: off.y };
    })(),
    sourcePosition: source,
    targetPosition: target,
    data: {
      id: node.id,
      text: node.text,
      parentId: node.parentId,
      order: node.order,
      collapsed: node.collapsed,
      textColor: node.textColor,
      bgColor: node.bgColor,
      borderColor: node.borderColor,
      shape: node.shape,
      isRoot: node.parentId == null,
      hasChildren: hasChildrenMap.get(node.id) ?? false,
      layoutDirection: globalDirection,
      link: node.link,
      fontSize: node.fontSize,
      lineHeight: node.lineHeight,
      boxShadow: node.boxShadow,
      borderStyle: node.borderStyle,
      borderWidth: node.borderWidth,
      borderRadius: node.borderRadius,
      styleTokens: node.styleTokens ? [...node.styleTokens] : undefined,
      isGhost: node.isGhost,
    },
    draggable: false,
    selectable: true,
  }));
}

// Legacy helper for unit tests (single root, no ELK, no offsets)
export function mindmapToReactFlow(mindmap: Mindmap): MindmapReactFlowState {
  const validMindmap = validateMindmap(mindmap);
  const positions: Record<string, { x: number; y: number }> = { [validMindmap.rootId]: { x: 0, y: 0 } };
  return {
    nodes: buildReactFlowNodes(validMindmap, positions),
    edges: buildReactFlowEdges(validMindmap),
  };
}
